import React, { useState, useRef, useEffect } from 'react';
import { Camera, AlertTriangle, FileText, Settings, History, ChevronDown, ChevronUp, Download, Shield, Wifi, WifiOff } from 'lucide-react';

function App() {
  const [activeTab, setActiveTab] = useState('camera');
  const [cameraActive, setCameraActive] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState({
    internetEnabled: true,
    industry: 'port',
    sensitivity: 'medium',
    notifications: true,
    useAI: true,
    apiEndpoint: 'https://port-hazard-api.eminsol.workers.dev'
  }); 
  const [expandedHazard, setExpandedHazard] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const knowledgeBase = {
    'container_stacking': {
      regulation: 'IMO CSS Code Section 2.4, OSHA 1917.71',
      recommendation: 'Reduce stack height to safe limits. Ensure proper alignment and twistlock engagement.',
      severity: 'high'
    },
    'damaged_container': {
      regulation: 'CSC Convention Annex I, OSHA 1917.71',
      recommendation: 'Remove from service immediately. Conduct structural inspection.',
      severity: 'critical'
    },
    'missing_ppe': {
      regulation: 'OSHA 1918.105, ILO C152 Article 16',
      recommendation: 'All personnel must wear hard hats, safety footwear, and high-vis clothing.',
      severity: 'high'
    },
    'improper_lashing': {
      regulation: 'CTU Code Chapter 3, CSS Code Section 4',
      recommendation: 'Re-secure cargo using proper lashing points. Verify equipment ratings.',
      severity: 'high'
    },
    'crane_clearance': {
      regulation: 'ISO 9927-1, OSHA 1917.45',
      recommendation: 'Maintain minimum 2 meter clearance. Stop operations and reposition.',
      severity: 'critical'
    }
  };

  const detectHazards = () => {
    const hazards = [
      { type: 'container_stacking', name: 'Unsafe Container Stacking', confidence: 0.89, description: 'Stack exceeds safe height limits', location: 'Center area', priority: 'Urgent' },
      { type: 'missing_ppe', name: 'Missing PPE', confidence: 0.92, description: 'Worker without hard hat and vest', location: 'Left foreground', priority: 'Immediate' },
      { type: 'damaged_container', name: 'Damaged Container', confidence: 0.78, description: 'Structural damage visible', location: 'Background right', priority: 'Urgent' }
    ];
    const selected = hazards.slice(0, Math.floor(Math.random() * 2) + 2);
    return selected.map(h => ({ ...h, ...knowledgeBase[h.type] }));
  };

  const detectHazardsWithAI = async (imageData) => {
    try {
      const apiEndpoint = settings.apiEndpoint;
      if (!apiEndpoint || apiEndpoint.includes('YOUR_SUBDOMAIN')) {
        console.log('Using local detection - no valid API endpoint');
        return detectHazards();
      }

      console.log('Calling API:', apiEndpoint);
      
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          image: imageData, 
          industry: settings.industry, 
          sensitivity: settings.sensitivity 
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error:', response.status, errorText);
        throw new Error(`API returned ${response.status}`);
      }

      const data = await response.json();
      console.log('API response:', data);
      
      if (!data.hazards || !Array.isArray(data.hazards)) {
        console.warn('Invalid API response format, using local detection');
        return detectHazards();
      }

      return data.hazards.map((h) => ({
        name: h.name || 'Unknown Hazard',
        type: h.type || 'general',
        severity: (h.severity || 'medium').toLowerCase(),
        confidence: (h.confidence || 85) / 100,
        description: h.description || 'Hazard detected',
        regulation: h.regulation || 'Multiple standards',
        recommendation: h.recommendation || 'Address this hazard',
        location: h.location || 'See image',
        priority: h.priority || 'Scheduled'
      }));
    } catch (error) {
      console.error('AI detection failed:', error);
      return detectHazards();
    }
  };

  const startCamera = async () => {
    try {
      setCameraError(null);
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraError('Camera API not available. Please use HTTPS or update your browser.');
        setCameraActive(false);
        return;
      }
      
      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } } 
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      }
      streamRef.current = stream;
      setCameraActive(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {
            videoRef.current.muted = true;
            videoRef.current.play();
          });
        }
      }, 200);
    } catch (err) {
      setCameraError(`Camera error: ${err.message}. Camera requires HTTPS on mobile devices.`);
      setCameraActive(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraActive(false);
  };

  const captureAndAnalyze = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (video.videoWidth === 0) {
      alert('Video not ready');
      return;
    }

    const maxWidth = 1920;
    const maxHeight = 1080;
    let width = video.videoWidth;
    let height = video.videoHeight;
    
    if (width > maxWidth) {
      height = (height * maxWidth) / width;
      width = maxWidth;
    }
    if (height > maxHeight) {
      width = (width * maxHeight) / height;
      height = maxHeight;
    }
    
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(video, 0, 0, width, height);
    
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedImage(imageDataUrl);
    setAnalyzing(true);

    try {
      const hazards = settings.useAI && settings.internetEnabled
        ? await detectHazardsWithAI(imageDataUrl)
        : detectHazards();

      const newResult = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        image: imageDataUrl,
        hazards,
        location: 'Port Terminal - Section A',
        totalHazards: hazards.length,
        analysisType: settings.useAI && settings.internetEnabled ? 'AI-Powered' : 'Local'
      };

      setAnalysisResults(newResult);
      setHistory(prev => [newResult, ...prev].slice(0, 20));
      setAnalyzing(false);
      setActiveTab('results');
      stopCamera();
    } catch (error) {
      console.error('Analysis error:', error);
      setAnalyzing(false);
      alert('Analysis failed: ' + error.message);
    }
  };

  const getSeverityColor = (s) => {
    const colors = { 
      critical: 'bg-red-600', 
      high: 'bg-orange-500', 
      medium: 'bg-yellow-500', 
      low: 'bg-blue-500' 
    };
    return colors[s?.toLowerCase()] || 'bg-gray-500';
  };

  const getSeverityBorder = (s) => {
    const borders = { 
      critical: 'border-red-600', 
      high: 'border-orange-500', 
      medium: 'border-yellow-500', 
      low: 'border-blue-500' 
    };
    return borders[s?.toLowerCase()] || 'border-gray-500';
  };

  const generateReport = (result) => {
    const htmlContent = `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Hazard Analysis Report - ${result.id}</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            max-width: 1200px;
            margin: 20px auto;
            padding: 20px;
            background: #f5f5f5;
        }
        .header {
            background: linear-gradient(135deg, #2563eb, #1e40af);
            color: white;
            padding: 30px;
            border-radius: 10px;
            margin-bottom: 20px;
        }
        .header h1 {
            margin: 0;
            font-size: 28px;
        }
        .header p {
            margin: 5px 0;
            opacity: 0.9;
        }
        .info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
            background: white;
            padding: 20px;
            border-radius: 10px;
            margin-bottom: 20px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .info-item {
            padding: 10px;
            border-left: 4px solid #2563eb;
            background: #f8fafc;
        }
        .info-label {
            font-weight: bold;
            color: #64748b;
            font-size: 12px;
            text-transform: uppercase;
        }
        .info-value {
            font-size: 16px;
            color: #1e293b;
            margin-top: 5px;
        }
        .image-container {
            background: white;
            padding: 20px;
            border-radius: 10px;
            margin-bottom: 20px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .image-container img {
            width: 100%;
            border-radius: 5px;
            display: block;
        }
        .summary {
            background: white;
            padding: 20px;
            border-radius: 10px;
            margin-bottom: 20px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            text-align: center;
        }
        .summary-count {
            font-size: 48px;
            font-weight: bold;
            color: ${result.totalHazards === 0 ? '#10b981' : '#dc2626'};
        }
        .hazard-card {
            background: white;
            padding: 20px;
            border-radius: 10px;
            margin-bottom: 20px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            border-left: 6px solid #dc2626;
        }
        .hazard-card.high { border-left-color: #f97316; }
        .hazard-card.medium { border-left-color: #eab308; }
        .hazard-card.low { border-left-color: #3b82f6; }
        .hazard-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 15px;
        }
        .hazard-title {
            font-size: 20px;
            font-weight: bold;
            color: #1e293b;
            flex: 1;
        }
        .severity-badge {
            padding: 6px 16px;
            border-radius: 20px;
            font-weight: bold;
            font-size: 12px;
            text-transform: uppercase;
            color: white;
        }
        .severity-critical { background: #dc2626; }
        .severity-high { background: #f97316; }
        .severity-medium { background: #eab308; }
        .severity-low { background: #3b82f6; }
        .hazard-section {
            margin: 15px 0;
        }
        .section-title {
            font-weight: bold;
            color: #64748b;
            font-size: 14px;
            margin-bottom: 8px;
        }
        .section-content {
            color: #475569;
            line-height: 1.6;
            padding: 10px;
            background: #f8fafc;
            border-radius: 5px;
        }
        .no-hazards {
            background: #d1fae5;
            color: #065f46;
            padding: 40px;
            text-align: center;
            border-radius: 10px;
            font-size: 18px;
        }
        .footer {
            text-align: center;
            color: #64748b;
            margin-top: 40px;
            padding: 20px;
            border-top: 2px solid #e2e8f0;
        }
        @media print {
            body { background: white; }
            .hazard-card { page-break-inside: avoid; }
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>🛡️ Port Safety Scanner - Hazard Analysis Report</h1>
        <p>HSE Inspection Report #${result.id}</p>
        <p>${new Date(result.timestamp).toLocaleString()}</p>
    </div>

    <div class="info-grid">
        <div class="info-item">
            <div class="info-label">Location</div>
            <div class="info-value">${result.location}</div>
        </div>
        <div class="info-item">
            <div class="info-label">Analysis Type</div>
            <div class="info-value">${result.analysisType}</div>
        </div>
        <div class="info-item">
            <div class="info-label">Industry</div>
            <div class="info-value">${settings.industry.toUpperCase()}</div>
        </div>
        <div class="info-item">
            <div class="info-label">Sensitivity Level</div>
            <div class="info-value">${settings.sensitivity.toUpperCase()}</div>
        </div>
    </div>

    <div class="image-container">
        <h2 style="margin-top: 0;">Inspected Area</h2>
        <img src="${result.image}" alt="Inspection Image">
    </div>

    <div class="summary">
        <div class="summary-count">${result.totalHazards}</div>
        <h2 style="margin: 10px 0;">Hazard${result.totalHazards !== 1 ? 's' : ''} Identified</h2>
    </div>

    ${result.hazards.length === 0 ? `
        <div class="no-hazards">
            <h2>✅ No Hazards Detected</h2>
            <p>The analyzed area appears to be safe with no visible safety concerns.</p>
        </div>
    ` : result.hazards.map((h, i) => `
        <div class="hazard-card ${h.severity.toLowerCase()}">
            <div class="hazard-header">
                <div class="hazard-title">Hazard #${i + 1}: ${h.name}</div>
                <span class="severity-badge severity-${h.severity.toLowerCase()}">${h.severity.toUpperCase()}</span>
            </div>

            <div class="hazard-section">
                <div class="section-title">📊 Details</div>
                <div class="section-content">
                    <strong>Type:</strong> ${h.type}<br>
                    <strong>Confidence:</strong> ${Math.round(h.confidence * 100)}%<br>
                    <strong>Location:</strong> ${h.location || 'See image above'}<br>
                    <strong>Priority:</strong> ${h.priority || 'Not specified'}
                </div>
            </div>

            <div class="hazard-section">
                <div class="section-title">⚠️ Description</div>
                <div class="section-content">${h.description}</div>
            </div>

            <div class="hazard-section">
                <div class="section-title">📋 Regulation Violated</div>
                <div class="section-content">${h.regulation}</div>
            </div>

            <div class="hazard-section">
                <div class="section-title">✅ Corrective Action Required</div>
                <div class="section-content">${h.recommendation}</div>
            </div>
        </div>
    `).join('')}

    <div class="footer">
        <p><strong>Port Safety Scanner v2.0</strong></p>
        <p>AI-Powered HSE Hazard Detection System</p>
        <p>Powered by GPT-4o Vision & Cloudflare Workers</p>
        <p style="margin-top: 10px; font-size: 12px;">
            This report was generated automatically. All findings should be verified by a qualified safety professional.
        </p>
    </div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hazard-report-${result.id}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => () => stopCamera(), []);

  return (
    <div className="flex flex-col h-screen bg-gray-900 text-white">
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-8 h-8" />
            <div>
              <h1 className="text-xl font-bold">Port Safety Scanner</h1>
              <p className="text-xs text-blue-100">HSE Hazard Detection</p>
            </div>
          </div>
          {settings.internetEnabled ? <Wifi className="w-5 h-5 text-green-300" /> : <WifiOff className="w-5 h-5 text-gray-300" />}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {activeTab === 'camera' && (
          <div className="flex flex-col h-full">
            <div className="flex-1 bg-black relative">
              {!cameraActive && !capturedImage && !cameraError && (
                <div className="flex items-center justify-center h-full p-4">
                  <div className="text-center w-full max-w-md">
                    <button onClick={startCamera} className="flex flex-col items-center gap-3 p-8 bg-blue-600 hover:bg-blue-700 rounded-lg w-full">
                      <Camera className="w-16 h-16" />
                      <span className="text-lg font-semibold">Start Camera</span>
                    </button>
                  </div>
                </div>
              )}

              {cameraError && (
                <div className="flex items-center justify-center h-full p-4">
                  <div className="bg-red-900 border border-red-600 rounded-lg p-6 max-w-md">
                    <AlertTriangle className="w-6 h-6 text-red-400 mb-2" />
                    <h3 className="font-bold text-lg mb-2">Camera Access Required</h3>
                    <p className="text-sm text-gray-300 mb-4">{cameraError}</p>
                    <button onClick={() => { setCameraError(null); startCamera(); }} className="w-full bg-blue-600 p-3 rounded-lg">Try Again</button>
                  </div>
                </div>
              )}
              
              {cameraActive && !analyzing && <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
              {capturedImage && !analyzing && <img src={capturedImage} alt="Captured" className="w-full h-full object-contain" />}
              {analyzing && (
                <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-lg font-semibold">Analyzing Image...</p>
                    <p className="text-sm text-gray-400 mt-2">AI inspection in progress</p>
                  </div>
                </div>
              )}
              <canvas ref={canvasRef} className="hidden" />
            </div>

            <div className="bg-gray-800">
              {cameraActive && !analyzing && (
                <div className="p-4 space-y-3">
                  <button onClick={captureAndAnalyze} className="w-full bg-red-600 hover:bg-red-700 p-5 rounded-lg font-bold text-xl flex items-center justify-center gap-3">
                    <AlertTriangle className="w-7 h-7" />ANALYZE FOR HAZARDS
                  </button>
                  <button onClick={stopCamera} className="w-full bg-gray-600 p-3 rounded-lg">Stop Camera</button>
                </div>
              )}
              {capturedImage && !analyzing && (
                <div className="p-4">
                  <button onClick={() => { setCapturedImage(null); setAnalysisResults(null); startCamera(); }} className="w-full bg-blue-600 p-4 rounded-lg">Capture New</button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'results' && analysisResults && (
          <div className="p-4 space-y-4">
            <img src={analysisResults.image} alt="Analysis" className="w-full rounded-lg" />
            <div className="bg-gray-800 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-bold">Analysis Results</h2>
                <span className={`px-3 py-1 rounded text-sm font-bold ${analysisResults.totalHazards === 0 ? 'bg-green-600' : 'bg-red-600'}`}>
                  {analysisResults.totalHazards} Hazard{analysisResults.totalHazards !== 1 ? 's' : ''}
                </span>
              </div>
              <p className="text-sm text-gray-300">{new Date(analysisResults.timestamp).toLocaleString()}</p>
              {analysisResults.analysisType && (
                <p className="text-xs text-blue-300 mt-1">
                  {analysisResults.analysisType === 'AI-Powered' ? '🤖 ' : '💻 '}
                  {analysisResults.analysisType}
                </p>
              )}
            </div>

            {analysisResults.hazards.length === 0 ? (
              <div className="bg-green-900 border border-green-600 rounded-lg p-6 text-center">
                <div className="text-5xl mb-3">✅</div>
                <h3 className="font-bold text-xl mb-2">No Hazards Detected</h3>
                <p className="text-sm text-green-200">The analyzed area appears to be safe</p>
              </div>
            ) : (
              <div className="space-y-3">
                {analysisResults.hazards.map((hazard, i) => (
                  <div key={i} className={`bg-gray-800 rounded-lg border-l-4 ${getSeverityBorder(hazard.severity)}`}>
                    <button onClick={() => setExpandedHazard(expandedHazard === i ? null : i)} className="w-full p-4 text-left">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${getSeverityColor(hazard.severity)}`}>
                            {(hazard.severity || 'medium').toUpperCase()}
                          </span>
                          {hazard.priority && (
                            <span className="ml-2 px-2 py-1 rounded text-xs bg-gray-700">
                              {hazard.priority}
                            </span>
                          )}
                          <h3 className="font-bold text-lg mt-2">{hazard.name}</h3>
                        </div>
                        {expandedHazard === i ? <ChevronUp className="mt-2" /> : <ChevronDown className="mt-2" />}
                      </div>
                      <p className="text-sm text-gray-300">{hazard.description}</p>
                      {hazard.confidence && (
                        <p className="text-xs text-gray-500 mt-1">Confidence: {Math.round(hazard.confidence * 100)}%</p>
                      )}
                      {hazard.location && (
                        <p className="text-xs text-gray-500">Location: {hazard.location}</p>
                      )}
                    </button>
                    {expandedHazard === i && (
                      <div className="px-4 pb-4 border-t border-gray-700 pt-3 space-y-2">
                        <div>
                          <h4 className="text-sm font-semibold text-yellow-400">📋 Regulation Violated</h4>
                          <p className="text-sm text-gray-300 mt-1">{hazard.regulation}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-green-400">✅ Corrective Action</h4>
                          <p className="text-sm text-gray-300 mt-1">{hazard.recommendation}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => generateReport(analysisResults)} className="w-full bg-green-600 hover:bg-green-700 p-4 rounded-lg flex items-center justify-center gap-2 font-semibold">
              <Download className="w-5 h-5" />Download HTML Report
            </button>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="p-4">
            <h2 className="text-xl font-bold mb-4">Analysis History</h2>
            {history.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <History className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-semibold">No History Yet</p>
                <p className="text-sm mt-2">Analyzed images will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map(item => (
                  <button key={item.id} onClick={() => { setAnalysisResults(item); setActiveTab('results'); }} className="w-full bg-gray-800 hover:bg-gray-700 p-4 rounded-lg text-left transition">
                    <div className="flex gap-3">
                      <img src={item.image} alt="Thumbnail" className="w-20 h-20 rounded object-cover" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${item.totalHazards === 0 ? 'bg-green-600' : 'bg-red-600'}`}>
                            {item.totalHazards} Hazard{item.totalHazards !== 1 ? 's' : ''}
                          </span>
                          {item.analysisType === 'AI-Powered' && <span className="text-xs text-blue-400">🤖 AI</span>}
                        </div>
                        <p className="text-sm text-gray-300">{new Date(item.timestamp).toLocaleString()}</p>
                        <p className="text-xs text-gray-500 mt-1">{item.location}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="p-4 space-y-4">
            <h2 className="text-xl font-bold mb-4">Settings</h2>
            <div className="bg-gray-800 p-4 rounded-lg space-y-4">
              <label className="flex items-center justify-between">
                <div>
                  <span className="font-semibold">AI-Powered Analysis</span>
                  <p className="text-xs text-gray-400">Use GPT-4o Vision for detection</p>
                </div>
                <input 
                  type="checkbox" 
                  checked={settings.useAI} 
                  onChange={e => setSettings({...settings, useAI: e.target.checked})} 
                  className="w-5 h-5" 
                />
              </label>
              <label className="flex items-center justify-between">
                <div>
                  <span className="font-semibold">Internet Connection</span>
                  <p className="text-xs text-gray-400">Enable online analysis</p>
                </div>
                <input 
                  type="checkbox" 
                  checked={settings.internetEnabled} 
                  onChange={e => setSettings({...settings, internetEnabled: e.target.checked})} 
                  className="w-5 h-5" 
                />
              </label>
              <div>
                <label className="block mb-2 font-semibold">API Endpoint</label>
                <input 
                  type="text" 
                  value={settings.apiEndpoint} 
                  onChange={e => setSettings({...settings, apiEndpoint: e.target.value})} 
                  className="w-full bg-gray-700 p-3 rounded text-white text-sm" 
                  placeholder="https://port-hazard-api.eminsol.workers.dev"
                />
              </div>
              <div>
                <label className="block mb-2 font-semibold">Industry Focus</label>
                <select 
                  value={settings.industry} 
                  onChange={e => setSettings({...settings, industry: e.target.value})} 
                  className="w-full bg-gray-700 p-3 rounded text-white"
                >
                  <option value="port">🚢 Port Terminal</option>
                  <option value="shipping">⚓ Shipping Operations</option>
                  <option value="cargo">📦 Cargo Handling</option>
                  <option value="manufacturing">🏭 Manufacturing</option>
                  <option value="construction">🏗️ Construction</option>
                  <option value="warehouse">🏢 Warehouse & Logistics</option>
                  <option value="oil-gas">⛽ Oil & Gas</option>
                  <option value="mining">⛏️ Mining Operations</option>
                </select>
              </div>
              <div>
                <label className="block mb-2 font-semibold">Detection Sensitivity</label>
                <select 
                  value={settings.sensitivity} 
                  onChange={e => setSettings({...settings, sensitivity: e.target.value})} 
                  className="w-full bg-gray-700 p-3 rounded text-white"
                >
                  <option value="low">Low - Major hazards only</option>
                  <option value="medium">Medium - Balanced detection</option>
                  <option value="high">High - All potential hazards</option>
                </select>
              </div>
            </div>
            <div className="bg-blue-900 border border-blue-700 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">About This App</h3>
              <p className="text-sm mb-1">Port Safety Scanner v2.0</p>
              <p className="text-xs text-gray-300">AI-powered HSE hazard detection for maritime and industrial operations</p>
              <p className="text-xs text-gray-400 mt-2">Powered by GPT-4o Vision & Cloudflare Workers</p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-gray-800 border-t border-gray-700 flex">
        <button 
          onClick={() => setActiveTab('camera')} 
          className={`flex-1 p-4 flex flex-col items-center gap-1 transition ${activeTab === 'camera' ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
        >
          <Camera className="w-6 h-6" />
          <span className="text-xs">Camera</span>
        </button>
        <button 
          onClick={() => setActiveTab('results')} 
          disabled={!analysisResults} 
          className={`flex-1 p-4 flex flex-col items-center gap-1 transition ${activeTab === 'results' ? 'bg-blue-600' : 'hover:bg-gray-700'} ${!analysisResults ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <FileText className="w-6 h-6" />
          <span className="text-xs">Results</span>
        </button>
        <button 
          onClick={() => setActiveTab('history')} 
          className={`flex-1 p-4 flex flex-col items-center gap-1 transition ${activeTab === 'history' ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
        >
          <History className="w-6 h-6" />
          <span className="text-xs">History</span>
        </button>
        <button 
          onClick={() => setActiveTab('settings')} 
          className={`flex-1 p-4 flex flex-col items-center gap-1 transition ${activeTab === 'settings' ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
        >
          <Settings className="w-6 h-6" />
          <span className="text-xs">Settings</span>
        </button>
      </div>
    </div>
  );
}

export default App;